import Project from "../project"
import {ProjectAccess} from "../../../gd/const/project"
import ProjectSession from "../project-session"
import canCreateTask from "./create-task"
import canCreateProject from "./create-project"
import canAddProjectUsers from "./can-add-project-users"
import canCreateEvent from "./create-event"
import {Access} from "../../../gd/const/access"
import {EntityType} from "../../../gd/const/item";
import canAddCompanyUsers from "./can-add-company-users"

// const debug = "ProjectAccessControl"
const debug = false

export class ProjectAccessControl {

    private project : ProjectSession

    constructor(project:ProjectSession) {
        this.project = project
    }

    get canModifyViews(): boolean {
        return gd.ac.project.canModifyViews(this.project.id)
    }

    // currently is used for page views only
    get canEditCover(): boolean {
        return this.hasFullAccess
    }

    // currently is used for page views only
    get canAddPage(): boolean {
        return this.hasFullAccess
    }

    canCreateEntity(entityType:EntityType):boolean {

        switch (entityType) {

            // case ParentsForType.TASK:
            case EntityType.TASK:
                return this.canCreateTask

            // case ParentsForType.EVENT:
            case EntityType.EVENT:
                return this.canCreateEvent

            // case ParentsForType.DOCUMENT:
            case EntityType.DOCUMENT:
                return this.canCreateDocument

            // case ParentsForType.DASHBOARD:
            case EntityType.DASHBOARD:
                return this.canCreateDashboard

            // case ParentsForType.DISCUSSION:
            case EntityType.CHAT:
                return this.canCreateDiscussion

            case EntityType.MEETING:
                return this.canCreateMeeting

            // case ParentsForType.REQUESTS_AND_FORMS:
            case EntityType.FORM:
                return this.canCreateRequestForms

            case EntityType.PROJECT     :
            // case ParentsForType.FOLDER      :
            // case ParentsForType.BACKLOG     :
            // case ParentsForType.SPRINT      :
                return this.canCreateProject

            case EntityType.GOAL:
                return this.canCreateGoal

            default:
                console.error("#09484732762 try chekc entity we do not support", entityType)
                return true
        }

    }

    get canCreateTask():boolean {

        if (!this.project.properties.canCreateTask) {
            debug && console.log(debug, "canCreateTask() - not allowed in properties")
            return false
        }

        return canCreateTask(this.project)
    }

    get canAddView():boolean {
        return this.hasFullAccess && this.project.properties.canHaveViews
    }

    get canCreateProject():boolean {
        if (!this.project.properties.canCreateProject) return false

        return canCreateProject(this.project)
    }

    get canAddProjectUsers(): boolean {
        return canAddProjectUsers(this.project)
    }

    get canAddCompanyUsers(): boolean {
        return canAddCompanyUsers(this.project)
    }

    /*get canCreateGroup():boolean {
        if (!this.project.properties.canCreateGroup) return false

        return canCreateProject(this.project)
    }*/

    /*get canCreatePage():boolean {

        if (!this.project.properties.canCreatePage) return false

        return canCreateProject(this.project)
    }*/

    get canCreateLink(): boolean {
        if (!this.project.properties.canCreateLink) return false

        return canCreateProject(this.project)
    }

    get canCreateEmbed(): boolean {
        if (!this.project.properties.canCreateEmbed) return false

        return canCreateProject(this.project)
    }

    get canCreateMeeting():boolean {
        if (!this.project.properties.canCreateMeeting) return false
        return this.hasLimitedAccess //this.hasStandardAccess
    }

    get canCreateEvent():boolean {
        if (!this.project.properties.canCreateEvent) return false

        return canCreateEvent(this.project)
    }

    get canCreateDiscussion():boolean {
        return this.project.properties.canCreateDiscussion && gd.ac.check.company.canCreateDiscussion()
    }

    get canCreateDocument():boolean {
        return this.project.properties.canCreateDocument && this.hasStandardAccess
    }

    get canCreateGoal():boolean {
        return this.project.properties.canCreateGoal
    }

    get canCreateDashboard():boolean {
        return this.project.properties.canCreateDashboard
    }

    get canCreateRequestForms():boolean {
        return this.project.properties.canCreateRequestForms
    }

    get canCreateAttachments():boolean {
        return this.project.properties.canCreateAttachments
    }

    get canChangeProjectName():boolean {
        return this.hasFullAccessAndOpen
    }

    get canChangeStatus():boolean {
        return this.hasFullAccess
    }

    get canChangeDuration():boolean {
        return this.canChangeEndDate
    }

    get canChangeDeadline():boolean {
        return this.hasFullAccessAndOpen
    }

    get canChangeStartDate():boolean {
        return this.hasFullAccessAndOpen
    }

    get canChangeEndDate():boolean {
        return this.hasFullAccessAndOpen
    }

    get canChangeIcon():boolean {
        return this.hasFullAccess
    }

    get canChangeHealth():boolean {
        return this.hasFullAccess
    }

    get canChangePriority():boolean {
        return this.hasFullAccess
    }

    get canChangeEstimate():boolean {
        return this.hasFullAccess
    }

    get canChangeProgress():boolean {
        return this.hasFullAccess
    }

    get canChangeProjectOwner():boolean {
        return this.hasFullAccess
    }

    get canChangeUsers():boolean {
        return this.hasFullAccess
    }

    get canChangeCustomFields():boolean {
        return this.hasFullAccess
    }

    get hasFullAccessAndOpen():boolean {
        if (!this.project.isOpen) return false
        return this.hasFullAccess
    }

    get hasFullAccess():boolean {

        const access = this.projectAccess

        if (access) {
            return access >= ProjectAccess.FULL
        }

        // template fix - not sure if good one but I think it's ok as undifined
        if (access === undefined && this.project.isTemplate && gd.company.role === gd.const.companyRole.MANAGER) {
            return true
        }

        return false
    }

    get hasClientAccess():boolean {

        const access = this.projectAccess

        if (access) {
            return access >= ProjectAccess.CLIENT
        }

        // template fix - not sure if good one but I think it's ok as undifined
        if (access === undefined && this.project.isTemplate && gd.company.role === gd.const.companyRole.MANAGER) {
            return true
        }

        return false
    }

    get hasStandardAccess():boolean {

        const access = this.projectAccess

        if (access) {
            return access >= Access.STANDARD
        }

        // template fix - not sure if good one but I think it's ok as undifined
        if (access === undefined && this.project.isTemplate && gd.company.role === gd.const.companyRole.MANAGER) {
            return true
        }

        return false
    }

    get hasLimitedAccess():boolean {

        const access = this.projectAccess

        if (access) {
            return access >= ProjectAccess.LIMITED
        }

        // template fix - not sure if good one but I think it's ok as undifined
        if (access === undefined && this.project.isTemplate && gd.company.role === gd.const.companyRole.MANAGER) {
            return true
        }

        return false
    }

    get projectAccess():ProjectAccess|undefined {

        // vozmozno this is session project vozmozno net
        if (this.project.access) return this.project.access

        const sessionProject = gd.session.projects.get(this.project.id) as Project

        if (sessionProject) return sessionProject.access

        if (!sessionProject) {
            console.error("#dkdjd2339038 no access possible")
            return undefined
        }


    }

    get canAddExpense() {
        return true // this.hasFullAccess
    }

    get canArchive():boolean {
        return this.project.properties.canArchive && !this.project.isArchived && this.hasFullAccess
    }

    get canUnArchive():boolean {
        return this.project.properties.canArchive && this.project.isArchived && this.hasFullAccess
    }
}

